import { useEffect, useRef, useState } from "react";
import { Mail, Send } from "lucide-react";
import { adminApi, getAdminToken, notifyUnreadRefresh } from "../../lib/adminApi";
import { useAuth } from "../../context/AuthContext";
import { useSiteContent } from "../../context/SiteContentContext";
import { sendContactReplyEmails } from "../../lib/email";

function formatDate(value) {
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mailtoHref(item) {
  const subject = encodeURIComponent(`Re: ${item.subject || "your enquiry"} — Creative Flare Yorkshire`);
  const body = encodeURIComponent(`Hi ${item.name.split(" ")[0] || item.name},\n\n`);
  return `mailto:${item.email}?subject=${subject}&body=${body}`;
}

export default function AdminContactsPage() {
  const { admin, can } = useAuth();
  const { org } = useSiteContent();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const selected = items.find((item) => item.id === selectedId) || null;
  const adminEmail = org?.email || admin?.email || "creativeflarebradford@yahoo.co.uk";
  const canEdit = can("contacts", "edit");

  const load = async () => {
    const data = (await adminApi.listContacts(getAdminToken())) || [];
    setItems(data);
    setSelectedId((current) => {
      if (current && data.some((item) => item.id === current)) return current;
      return data[0]?.id || null;
    });
  };

  const loadMessages = async (item) => {
    if (!item) {
      setMessages([]);
      return;
    }
    try {
      const rows = await adminApi.listContactMessages(getAdminToken(), item.id);
      setMessages(rows || []);
    } catch {
      setMessages([
        {
          id: `${item.id}-original`,
          sender_type: "visitor",
          sender_name: item.name,
          body: item.message,
          created_at: item.created_at,
          email_sent: item.email_sent,
        },
      ]);
    }
  };

  useEffect(() => {
    load().catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    loadMessages(selected);
  }, [selectedId, selected]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const openThread = async (item) => {
    setSelectedId(item.id);
    setNotice("");
    if (item.status === "new") {
      try {
        await adminApi.updateContact(getAdminToken(), item.id, "read");
        notifyUnreadRefresh();
        await load();
      } catch {
        // listing still works if status update fails
      }
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!selected || !reply.trim()) return;
    setSending(true);
    setNotice("");
    setError("");
    const body = reply.trim();
    try {
      const result = await adminApi.replyContact(getAdminToken(), selected.id, body);
      try {
        await sendContactReplyEmails({
          visitorName: result.visitor_name || selected.name,
          visitorEmail: result.visitor_email || selected.email,
          subject: result.subject || selected.subject,
          replyBody: body,
          senderName: result.sender_name || admin?.username,
          adminEmail,
        });
        if (result.id) {
          await adminApi.markContactMessageEmailed(getAdminToken(), result.id).catch(() => {});
        }
        setNotice("Reply emailed to the visitor (check their inbox and spam). A copy is in the admin inbox — you can also reply from email.");
      } catch (emailErr) {
        console.warn(emailErr);
        setError(emailErr.message || "Reply saved in the chat, but the email could not be sent. Use Reply in email.");
      }
      setReply("");
      notifyUnreadRefresh();
      await load();
    } catch (err) {
      setError(err.message || "Could not send reply. Run the latest Supabase SQL if chat replies are new.");
    } finally {
      setSending(false);
    }
  };

  if (error && items.length === 0) return <p className="text-[#BE185D]">{error}</p>;

  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#EC4899]">Inbox</p>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Contact messages</h1>
      <p className="text-sm text-black/55 mt-2 max-w-2xl">
        Conversations appear as a chat. Replies email the sender and leave a copy in the admin inbox that you can reply to from email as well.
      </p>

      <div className="grid lg:grid-cols-12 gap-6 mt-8">
        <div className="lg:col-span-4 rounded-3xl bg-white border border-black/5 shadow-ambient overflow-hidden max-h-[72vh] overflow-y-auto">
          {items.length === 0 && <p className="p-6 text-black/50">No messages yet.</p>}
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => openThread(item)}
                  className={`w-full text-left px-5 py-4 border-b border-black/5 ${
                    selectedId === item.id ? "bg-[#FDF2F8]" : "hover:bg-black/[0.02]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium truncate">{item.name}</span>
                    <span
                      className={`font-mono text-[10px] uppercase tracking-widest shrink-0 ${
                        item.status === "new" ? "text-[#EC4899]" : "text-black/40"
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>
                  <div className="text-sm text-black/60 mt-1 truncate">{item.subject}</div>
                  <div className="text-xs text-black/40 mt-1">{formatDate(item.created_at)}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-8 rounded-3xl bg-white border border-black/5 shadow-ambient flex flex-col min-h-[72vh] max-h-[72vh]">
          {!selected ? (
            <p className="text-black/50 m-auto p-8">Select a message to open the conversation.</p>
          ) : (
            <>
              <div className="shrink-0 px-5 py-4 border-b border-black/5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display text-3xl">{selected.name}</h2>
                    <a href={`mailto:${selected.email}`} className="text-[#EC4899] text-sm">
                      {selected.email}
                    </a>
                    <p className="text-sm text-black/50 mt-1">{selected.subject}</p>
                  </div>
                  {canEdit && (
                  <a
                    href={mailtoHref(selected)}
                    className="inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-2 text-sm hover:border-[#EC4899] hover:text-[#EC4899]"
                  >
                    <Mail size={14} />
                    Reply in email
                  </a>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#FAFAFA]">
                {messages.map((m) => {
                  const mine = m.sender_type === "admin";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                          mine
                            ? "bg-[#EC4899] text-white rounded-br-md"
                            : "bg-white border border-black/5 text-black rounded-bl-md"
                        }`}
                      >
                        <p className={`text-[11px] mb-1 ${mine ? "text-white/80" : "text-black/40"}`}>
                          {m.sender_name || (mine ? "Admin" : selected.name)} · {formatDate(m.created_at)}
                          {m.email_sent ? " · emailed" : ""}
                        </p>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={sendReply} className="shrink-0 border-t border-black/5 p-4 bg-white">
                {error && <p className="text-sm text-[#BE185D] mb-2">{error}</p>}
                {notice && <p className="text-sm text-emerald-700 mb-2">{notice}</p>}
                {canEdit ? (
                  <>
                <textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type a reply… this emails the sender and copies the admin inbox"
                  className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none focus:border-[#EC4899]"
                />
                <div className="flex justify-end mt-3">
                  <button
                    type="submit"
                    disabled={sending || !reply.trim()}
                    className="inline-flex items-center gap-2 rounded-full bg-[#EC4899] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#DB2777] disabled:opacity-50"
                  >
                    <Send size={14} />
                    {sending ? "Sending…" : "Send reply"}
                  </button>
                </div>
                  </>
                ) : (
                  <p className="text-sm text-black/50">
                    You can read this conversation, but this account is not allowed to send replies.
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
